// externals
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    RADIO_BUTTON_LIST,
    RADIO_BUTTON_LIST_LABEL,
    RATING_DISPLAY_NAME,
    RATING_INPUT,
    SELF_REPORTED_CREDIT_RATING_FIELD,
    TIER,
    RATING
} from '../../constants';
import { kebabCase } from 'lodash';
import type { CreditRatingDisplayType } from '@makemydeal/dr-dash-types';
import { NumericInput } from '@interstate/components/NumericInput';
import { RadioButtonList } from '@interstate/components/RadioButtonList';
import { SelectInput } from '@interstate/components/SelectInput';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import { dealLockHooks } from '../../hooks';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { CreditRatingContainer, RadioButtonContainer, InputWrapper } from './SelfReportedCreditRatingWithScore.styles';

interface SelfReportedCreditRatingWithScoreProps {
    isLenderFormDirtyRef?: React.MutableRefObject<boolean>;
    stacked?: boolean;
}

export const SelfReportedCreditRatingWithScore = ({ isLenderFormDirtyRef, stacked }: SelfReportedCreditRatingWithScoreProps) => {
    const dispatch = useDispatch();
    const options = useSelector(offerSelectors.getCreditTiersAsOptions);
    const selected = useSelector(offerSelectors.getSelectedCreditTier);
    const manualCreditScoreFromState = useSelector(offerReduxSelectors.getManualCreditScore);
    const programsCreditScoreOverride = useSelector(programsSelectors.getCreditScoreOverride);
    const displayedCreditScore = programsCreditScoreOverride ?? manualCreditScoreFromState;
    const manualCreditScoreAsString = displayedCreditScore ? displayedCreditScore.toString() : '';
    const persistedCreditDisplayType = useSelector(offerInfoSelectors.getCreditRatingDisplayType);
    const getInitialDisplay = persistedCreditDisplayType ?? (displayedCreditScore ? RATING_DISPLAY_NAME : TIER);
    const [creditDisplayType, setCreditDisplayType] = React.useState(getInitialDisplay);
    const [tempCreditScoreInput, setTempCreditScoreInput] = React.useState('');
    const [shouldClearCreditScoreInput, setShouldClearCreditScoreInput] = React.useState(false);
    const [isCreditFieldTouched, setIsCreditFieldTouched] = React.useState(false);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isLocked = dealLockHooks.useIsDealLocked();
    const creditValidationState = useSelector(
        dealScreenErrorSelectors.getFieldValidationError(DealTabs.LENDER, SELF_REPORTED_CREDIT_RATING_FIELD)
    );

    const reactOnUpdatedCreditScore = (creditScore: string) => {
        if (isValidCreditScore(creditScore) && isNewCreditScore()) {
            const creditRatingAsNum = parseInt(creditScore);
            dispatch(offerReduxActionCreators.updateManualCreditScore(creditRatingAsNum));
        }
    };

    useEffect(() => {
        reactOnUpdatedCreditScore(tempCreditScoreInput);
    }, [tempCreditScoreInput]);

    const isValidCreditScore = (score: string): boolean => {
        const num = parseInt(score, 10);
        return !isNaN(num) && num >= 300 && num <= 900;
    };

    const isNewCreditScore = () => {
        const manualAsString = manualCreditScoreFromState ? manualCreditScoreFromState.toString() : '';
        return tempCreditScoreInput !== manualAsString;
    };

    const { hasError, errorMessage } = useMemo(() => {
        if (creditDisplayType !== RATING_DISPLAY_NAME) {
            return { hasError: false, errorMessage: undefined };
        }
        const score = tempCreditScoreInput || manualCreditScoreAsString;
        const num = parseInt(score, 10);

        if (num < 100 && isCreditFieldTouched) {
            return { hasError: true, errorMessage: '3 characters required.' };
        } else if (num >= 100 && num < 300 && isCreditFieldTouched) {
            return { hasError: true, errorMessage: `'${score}' is less than the minimum value of 300.` };
        } else if (num > 900 && !shouldClearCreditScoreInput) {
            return { hasError: true, errorMessage: `'${score}' exceeds the maximum value of 900.` };
        }
        return { hasError: false, errorMessage: undefined };
    }, [creditDisplayType, tempCreditScoreInput, manualCreditScoreAsString, isCreditFieldTouched, shouldClearCreditScoreInput]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && creditValidationState !== hasError) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.LENDER, SELF_REPORTED_CREDIT_RATING_FIELD, hasError));
        }
    }, [hasError]);

    // Track dirty state for unsaved changes modal
    useEffect(() => {
        if (isLenderFormDirtyRef) {
            isLenderFormDirtyRef.current = hasError;
        }
    }, [hasError]);

    const handleSelectInputChange = (event: any) => {
        dispatch(offerReduxActionCreators.selectedCreditTier(event.target.value));
        dispatch(interactionAnalyticsActionCreators.offerCreditRatingDropdown());
    };

    const handleInputChange = (event: any) => {
        setTempCreditScoreInput(event.target.value);
        setShouldClearCreditScoreInput(false);
        // Reset touched state when user starts editing to prevent error message from showing until input is complete
        if (isCreditFieldTouched) {
            setIsCreditFieldTouched(false);
        }
    };
    const handleInputFocus = () => {
        setShouldClearCreditScoreInput(true);
        if (isCreditFieldTouched) {
            setIsCreditFieldTouched(false);
        }
    };
    const handleInputBlur = () => {
        if (!isCreditFieldTouched) {
            setIsCreditFieldTouched(true);
        }
        setTempCreditScoreInput(tempCreditScoreInput);
        setShouldClearCreditScoreInput(false);
    };

    return (
        <CreditRatingContainer stacked={stacked} data-testid="self-reported-credit-score">
            <RadioButtonContainer stacked={stacked}>
                <RadioButtonList
                    data-testid={RADIO_BUTTON_LIST}
                    label={RADIO_BUTTON_LIST_LABEL}
                    name={RADIO_BUTTON_LIST}
                    value={creditDisplayType}
                    onChange={(e: any) => {
                        // The radio list only ever emits TIER or RATING, but `e` is `any`; narrow here so the
                        // switch below and the typed action both receive a real CreditRatingDisplayType.
                        const updatedCreditDisplayType: CreditRatingDisplayType =
                            e.target.value === TIER ? TIER : RATING_DISPLAY_NAME;

                        switch (updatedCreditDisplayType) {
                            case RATING_DISPLAY_NAME: {
                                const tempCreditScore = tempCreditScoreInput || manualCreditScoreAsString;

                                if (!shouldClearCreditScoreInput && tempCreditScore) {
                                    reactOnUpdatedCreditScore(tempCreditScore);
                                }

                                break;
                            }
                            case TIER: {
                                dispatch(offerReduxActionCreators.selectedCreditTier(selected));

                                break;
                            }
                        }

                        setCreditDisplayType(updatedCreditDisplayType);
                        dispatch(offerActionCreators.setCreditRatingDisplayType(updatedCreditDisplayType));
                    }}
                    options={[
                        {
                            label: TIER,
                            value: TIER,
                            disabled: isLocked,
                            checkedDisabled: isLocked
                        },
                        {
                            label: RATING,
                            value: RATING,
                            disabled: isLocked,
                            checkedDisabled: isLocked
                        }
                    ]}
                    inline={true}
                />
            </RadioButtonContainer>
            {creditDisplayType === RATING_DISPLAY_NAME ? (
                <InputWrapper stacked={stacked}>
                    <NumericInput
                        size="sm"
                        id={RATING_INPUT}
                        data-testid={RATING_INPUT}
                        label={RATING_DISPLAY_NAME}
                        name={RATING_INPUT}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        value={shouldClearCreditScoreInput ? '' : tempCreditScoreInput || manualCreditScoreAsString}
                        minLength={3}
                        maxLength={3}
                        hasError={hasError}
                        errorMessage={errorMessage}
                        disabled={isLocked}
                    />
                </InputWrapper>
            ) : (
                <InputWrapper stacked={stacked}>
                    <SelectInput
                        size="sm"
                        id={kebabCase(SELF_REPORTED_CREDIT_RATING_FIELD)}
                        name={kebabCase(SELF_REPORTED_CREDIT_RATING_FIELD)}
                        onChange={handleSelectInputChange}
                        value={selected}
                        options={options}
                        displayDeselectOption={false}
                        label={TIER}
                        disabled={isLocked}
                    />
                </InputWrapper>
            )}
        </CreditRatingContainer>
    );
};

export default SelfReportedCreditRatingWithScore;
